import { Inject, Injectable } from "@angular/core";
import { ProgressDialogServiceBase } from "@ankaadia/ankaadia-shared-frontend";
import type { ProgressDialogHandle, ProgressDialogOptions } from "@ankaadia/ankaadia-shared-frontend";
import { TranslocoService } from "@jsverse/transloco";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { Subject } from "rxjs";
import { NewUiProgressDialogComponent } from "../components/progress-dialog/new-ui-progress-dialog.component";

@Injectable({ providedIn: "root" })
export class ProgressDialogService extends ProgressDialogServiceBase {
  static override instance: ProgressDialogService;

  private dialogRef: DynamicDialogRef | null = null;
  private cancelledSubject: Subject<void> | null = null;

  constructor(
    @Inject(DialogService)
    private readonly dialogService: DialogService,
    private readonly transloco: TranslocoService
  ) {
    super();
    ProgressDialogService.instance = this;
    ProgressDialogServiceBase.instance = this;
  }

  open(title: string, progressId: string, options?: ProgressDialogOptions): ProgressDialogHandle {
    this.close();

    this.cancelledSubject = new Subject<void>();
    const translatedTitle = this.transloco.translate(title);

    this.dialogRef = this.dialogService.open(NewUiProgressDialogComponent, {
      header: translatedTitle,
      data: {
        progressId,
        cancelable: options?.cancelable ?? true,
        mode: options?.mode ?? "activity-indicator",
      },
      closable: false,
      focusOnShow: false,
      modal: true,
      appendTo: "body",
    });

    this.dialogRef?.onClose?.subscribe(() => {
      this.cancelledSubject?.next();
      this.cancelledSubject?.complete();
      this.cancelledSubject = null;
      this.dialogRef = null;
    });

    return {
      cancelled$: this.cancelledSubject.asObservable(),
    };
  }

  close(): void {
    if (this.dialogRef) {
      const ref = this.dialogRef;
      this.dialogRef = null;
      this.cancelledSubject = null;
      ref.close();
    } else {
      // If dialogRef is null (e.g. open() returned null due to duplication check),
      // still clean up the cancelledSubject to avoid Subject leaks.
      this.cancelledSubject?.complete();
      this.cancelledSubject = null;
    }
  }
}
